import React from "react";
import { CompanyDetails } from "./CompanyDetails";
import { CompanyContacts } from "./CompanyContacts";
import { CompanyTasks } from "./CompanyTasks";
import { CompanyTickets } from "./CompanyTickets";
import { CompanyActivities } from "./CompanyActivities";
import { CompanyDrive } from "./CompanyDrive";
import { CompanyDocumentation } from "./CompanyDocumentation";
import { CompanyClientDocuments } from "./CompanyClientDocuments";
import { CompanyQuickBooksTab } from "./CompanyQuickBooksTab";
import { CompanyTimeEntries } from "./CompanyTimeEntries";
import { CompanyDeals } from "./CompanyDeals";
import { CompanyRecurringTransactionsTab } from "@/features/recurring-transactions/components/CompanyRecurringTransactionsTab";
import { CompanyOverview } from "./overview/CompanyOverview";
import { CompanyContractsTab } from "@/features/contracts/components/tabs/CompanyContractsTab";
import { CompanySupportLinkCard } from "@/components/companies/CompanySupportLinkCard";
import { CompanySupportInboxDisplay } from "@/features/companies/support-inbox/CompanySupportInboxDisplay";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { CompanyEntity } from "@/lib/types/company";
import { EntityAuditHistoryTab } from "@/components/audit/EntityAuditHistoryTab";

// ============================================================================
// COMPANY DETAIL TAB COMPONENTS
// ============================================================================
// Maps tab component names to actual React components for the UniversalDetailPage

export const CompanyDetailTabComponents = {
  CompanyDetailsDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyOverview company={item} {...props} />
  ),

  CompanyContactsDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyContacts companyId={item.id} {...props} />
  ),

  CompanyTasksDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyTasks companyId={item.id} {...props} />
  ),

  CompanyTicketsDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyTickets companyId={item.id} {...props} />
  ),

  CompanyTimeEntriesDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyTimeEntries companyId={item.id} companyName={item.name} {...props} />
  ),

  CompanyRecurringTransactionsDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyRecurringTransactionsTab companyId={item.id} {...props} />
  ),

  CompanyTimelineDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyActivities
      companyId={item.id}
      companyName={item.name}
      {...props}
    />
  ),

  CompanyDriveDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyDrive companyId={item.id} company={item} {...props} />
  ),

  CompanyDocumentationDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyDocumentation companyId={item.id} {...props} />
  ),

  // Client-facing, customer-safe MSP documentation (the `client-documents` tab).
  // Distinct from CompanyDocumentationDisplay (internal authoring) and
  // CompanyDriveDisplay (internal Drive contents) — see CompanyDetailConfig.ts.
  CompanyClientDocumentsDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyClientDocuments companyId={item.id} companyName={item.name} {...props} />
  ),

  CompanyQuickBooksDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyQuickBooksTab company={item} {...props} />
  ),

  CompanyDealsDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyDeals company={item} {...props} />
  ),

  CompanyContractsDisplay: ({ item, ...props }: TabContentProps<CompanyEntity>) => (
    <CompanyContractsTab companyId={item.id} companyName={item.name} {...props} />
  ),

  CompanySupportLinkDisplay: ({ item }: TabContentProps<CompanyEntity>) => (
    <CompanySupportLinkCard companyId={item.id} companyName={item.name} />
  ),

  CompanySupportInboxDisplay: ({ item }: TabContentProps<CompanyEntity>) => (
    <CompanySupportInboxDisplay companyId={item.id} companyName={item.name} />
  ),

  // The audit feed's SECOND entry point (the first is the "View history & audit"
  // place-modal). Both render `EntityAuditFeed` — one implementation, two entry
  // points. `EntityAuditHistoryTab` carries the empty-parent guard.
  CompanyHistoryDisplay: ({ item }: TabContentProps<CompanyEntity>) => (
    <EntityAuditHistoryTab
      entityType="companies"
      entityId={item.id}
      subjectLabel={item.name ?? "Company"}
      entityNoun="company"
    />
  ),
};
